import React from 'react';
import {
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Box,
  Typography,
  Chip,
  Tooltip
} from '@mui/material';
import {
  MarkEmailRead as MarkEmailReadIcon,
  MarkEmailUnread as MarkEmailUnreadIcon,
  Assignment as AssignmentIcon,
  Payment as PaymentIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  ChangeCircle as ChangeCircleIcon,
  Comment as CommentIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Notification, NotificationType } from '../../services/api/notifications/notificationsApi';
import { buildNotificationNavigationUrl } from '../../utils/navigationUtils';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (notificationId: number) => void;
  onMarkAsUnread: (notificationId: number) => void;
  /**
   * Se true, mostra ações reduzidas (apenas marcar como lida/não lida)
   * @default false
   */
  compact?: boolean;
  /**
   * Callback para fechar o dropdown quando navegar
   */
  onClose?: () => void;
}

/**
 * Componente individual de notificação
 * Exibe título, mensagem, timestamp e ações
 */
const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onMarkAsUnread,
  compact = false,
  onClose
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Mapear tipos para ícones e cores
  const getNotificationConfig = (type: NotificationType) => {
    const configs = {
      PROJECT_ASSIGNED: {
        icon: <AssignmentIcon />,
        color: theme.palette.primary.main,
        bgColor: theme.palette.primary.light + '20'
      },
      PAYMENT_RECEIVED: {
        icon: <PaymentIcon />,
        color: theme.palette.success.main,
        bgColor: theme.palette.success.light + '20'
      },
      PAYMENT_OVERDUE: {
        icon: <WarningIcon />,
        color: theme.palette.error.main,
        bgColor: theme.palette.error.light + '20'
      },
      PROJECT_AUTO_CREATED: {
        icon: <AddIcon />,
        color: theme.palette.info.main,
        bgColor: theme.palette.info.light + '20'
      },
      STAGE_CHANGED: {
        icon: <ChangeCircleIcon />,
        color: theme.palette.warning.main,
        bgColor: theme.palette.warning.light + '20'
      },
      COMMENT_ADDED: {
        icon: <CommentIcon />,
        color: theme.palette.secondary.main,
        bgColor: theme.palette.secondary.light + '20'
      },
      COMMENT_MENTION: {
        icon: <CommentIcon />,
        color: theme.palette.warning.main,
        bgColor: theme.palette.warning.light + '20'
      }
    };

    return configs[type] || configs.PROJECT_ASSIGNED;
  };

  const config = getNotificationConfig(notification.type);
  
  // Prioridade para cor do texto
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
      case 'URGENT':
        return theme.palette.error.main;
      case 'MEDIUM':
        return theme.palette.warning.main;
      default:
        return theme.palette.text.primary;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { 
        addSuffix: true, 
        locale: ptBR 
      });
    } catch {
      return 'Data inválida';
    }
  };

  const handleMarkAsRead = (event: React.MouseEvent) => {
    event.stopPropagation();
    onMarkAsRead(notification.id);
  };

  const handleMarkAsUnread = (event: React.MouseEvent) => {
    event.stopPropagation();
    onMarkAsUnread(notification.id);
  };


  const handleNotificationClick = () => {
    // Verificar se a notificação tem entityId (projeto) para navegação
    if (!notification.entityId) {
      console.warn('Notificação sem entityId, não é possível navegar');
      return;
    }

    // Construir URL de navegação baseada no tipo da notificação
    const navigationUrl = buildNotificationNavigationUrl(notification.type, notification.entityId);
    
    if (navigationUrl) {
      // Marcar como lida automaticamente ao clicar
      if (!notification.isRead) {
        onMarkAsRead(notification.id);
      }
      
      // Fechar dropdown/modal sempre (independente da navegação)
      if (onClose) {
        onClose();
      }
      
      // Navegar para a URL construída
      navigate(navigationUrl);
    } else {
      console.warn(`Tipo de notificação não suportado para navegação: ${notification.type}`);
    }
  };

  // Verificar se a notificação é clicável (tem navegação configurada)
  const isClickable = notification.entityId && buildNotificationNavigationUrl(notification.type, notification.entityId) !== null;

  return (
    <ListItem
      disablePadding
      sx={{
        backgroundColor: notification.isRead 
          ? 'transparent' 
          : theme.palette.mode === 'dark' 
            ? theme.palette.grey[800]
            : theme.palette.grey[100],
        borderLeft: notification.isRead ? 'none' : `4px solid ${config.color}`,
      }}
    >
      <ListItemButton
        onClick={isClickable ? handleNotificationClick : undefined}
        disabled={!isClickable}
        sx={{
          px: 2,
          py: 1.5,
          minHeight: 80,
          cursor: isClickable ? 'pointer' : 'default',
          '&:hover': {
            backgroundColor: isClickable 
              ? theme.palette.action.hover 
              : 'transparent',
          },
          '&.Mui-disabled': {
            opacity: 1,
            backgroundColor: 'transparent'
          }
        }}
      >
      {/* Ícone da notificação */}
      <Box
        sx={{
          mr: 2,
          display: 'flex',
          alignItems: 'flex-start',
          mt: 0.5,
          color: config.color
        }}
      >
        {config.icon}
      </Box>

      {/* Conteúdo principal */}
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Typography
              variant="subtitle2"
              fontWeight={notification.isRead ? 'normal' : 'bold'}
              color={getPriorityColor(notification.priority)}
              sx={{ flexGrow: 1 }}
            >
              {notification.title}
            </Typography>
          </Box>
        }
        secondary={
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 0.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {notification.message}
            </Typography>
            
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="caption" color="text.disabled">
                {formatTimeAgo(notification.createdAt)}
              </Typography>
              
              {notification.priority === 'HIGH' || notification.priority === 'URGENT' ? (
                <Chip
                  label={notification.priority === 'URGENT' ? 'Urgente' : 'Alta'}
                  size="small"
                  color="error"
                  sx={{ height: 16, fontSize: '0.625rem' }}
                />
              ) : null}
            </Box>
          </Box>
        }
        sx={{ pr: compact ? 6 : 10 }}
      />

      </ListItemButton>
      
      {/* Ações fora do ListItemButton para funcionar corretamente */}
      <ListItemSecondaryAction>
        <Box display="flex" gap={0.5}>
          {!notification.isRead ? (
            <Tooltip title="Marcar como lida" arrow>
              <IconButton
                size="small"
                onClick={handleMarkAsRead}
                sx={{ color: theme.palette.primary.main }}
              >
                <MarkEmailReadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Marcar como não lida" arrow>
              <IconButton
                size="small"
                onClick={handleMarkAsUnread}
                sx={{ color: theme.palette.warning.main }}
              >
                <MarkEmailUnreadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

export default NotificationItem;
